import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import EditProfileCard from "./EditProfileCard";
import DeviceActivityCard from "./DeviceActivityCard";
import ImageCropper from "../ImageCropper/ImageCropper";
import {
  IoClose,
  IoPencil,
  IoAdd,
} from "react-icons/io5";
import { Toaster } from "../Toaster/Toaster";
import {
  getUserProfileDetails,
  modifyUserProfile,
  uploadUserFile,
} from "../../service/AuthWellDhanService";
import { useUtils } from "../../context/UtilsContext";
import { ApiContext } from "../../context/MyContext";
import { formatLocalDate } from "../../utils/formatLocalDate";
import {
  getLatestEntry,
  calculateAge,
  calculateBMI,
  calculateIdealWeight,
  calculateBMR,
  calculateTDEE,
} from "../../utils/bodyMetrics";
import { profileCache } from "../../utils/profileCache";
import { myUserProfileCache, serializeUserNotesKey } from "../../utils/listCache/caches";
import { Tabs } from "../../pages/comms/Broadcasts/ui/Tabs";
import NotesTab from "../../features/notes/workflow/profile/view/NotesTab";
import { useMyLeaves } from "../../features/leaves/workflow/useMyLeaves";
import LeaveTab, { NO_MANAGER_MESSAGE } from "../../features/leaves/ui/LeaveTab";
import ApplyLeaveModal from "../../features/leaves/ui/ApplyLeaveModal";
import { isCoachEmployee, isCoachManager } from "../../pages/comms/Bookings/coachRoster";
import { isExecutiveEmployee } from "../../utils/accessControl";

const defaultProfile = {
  firstName: "John",
  lastName: "Doe",
  phoneNumber: "+91 0000000000",
  email: "unknown@email.com",
  location: "Unknown",
  gender: "Not specified",
  height: 0,
  height_id: 0,
  weight_id: 0,
  weight: 0,
  heightUnits: "cm",
  weightUnits: "kg",
  dietaryPreferences: [],
  medicalHistories: [],
  otherMedicalConditions: [],
  device: null,
  fitnessGoals: [],
  physicalInjury: "None",
  profilePic: "",
  profilePicPath: "",
  profilePicUploadedAt: 0,
  profile_photo_id: null,
  profilePhotoUpdated: false,
  profilePhotoRemoved: false,
  certifications: [],
  profilePicPreview: "",
  achievements: [],
  languages: [],
  professionalSpecialties: [],
};

/**
 * @param {Object} props
 * @param {string} [props.profileId] - User whose profile to render; omitted
 *   means the signed-in coach's own profile.
 * @param {string|null} [props.cacheKey] - When set, this client's profile
 *   payload is served from and written to `myUserProfileCache` under this key.
 *   Opt-in per call site: user profiles are otherwise always fetched fresh,
 *   so `/dashboard/:id` and the comms rails are unaffected by leaving it null.
 * @param {boolean} [props.enableNotesTab=false] - Adds a Profile/Notes tab
 *   switcher inside the standalone slide-over drawer (the `onClose`-less
 *   branch), so the drawer's own "Notes" (no call-duration field, coach-
 *   neutral copy) sits beside the existing banner content under a "Profile"
 *   tab. Set on `/dashboard/:id`, the Assign Diet Plan flow, the diet plan
 *   detail page, and both workout pages — left false on `ClientRail` and
 *   `AppSidebar`.
 */
const ProfileCard = ({
  profileId,
  onClose,
  showProfilePicture = true,
  showPhotoControls = false,
  cacheKey = null,
  enableNotesTab = false,
}) => {
  const [error, setError] = useState(false);
  const [profileData, setProfileData] = useState(defaultProfile);
  const [originalData, setOriginalData] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("default");
  const [cropperModalOpen, setCropperModalOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [dietaryTagsExpanded, setDietaryTagsExpanded] = useState(false);
  const [focusTagsExpanded, setFocusTagsExpanded] = useState(false);
  const [medicalTagsExpanded, setMedicalTagsExpanded] = useState(false);
  const [otherMedicalTagsExpanded, setOtherMedicalTagsExpanded] = useState(false);
  // Card always starts expanded/inline for a newly opened client; collapsing is a
  // per-view choice, not persisted, so switching clients always shows full details first.
  // Drawer starts closed: the floating icon is the entry point, nothing is shown until clicked.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Only read when `enableNotesTab` is set; unused (and harmless) otherwise.
  const [activeCardTab, setActiveCardTab] = useState("profile");
  // Own-profile modal (onClose branch) only: active tab, plus the
  // Professional tab's inline "+ Add" row (which section, and its draft).
  const [ownTab, setOwnTab] = useState("health");
  const [addingSection, setAddingSection] = useState(null);
  const [addDraft, setAddDraft] = useState("");
  const fileInputRef = useRef(null);
  const profileDataSnapshotRef = useRef(null);

  const { sessionUtils, profileUtils } = useUtils();
  const { refreshUserProfile, employeeData, departmentsWithRoles } = useContext(ApiContext);

  // Leave lives on the user's own profile only (the onClose modal), for
  // coaches, coach managers and executives. Executives may apply without an
  // assigned manager, since any executive can approve a leave; everyone else
  // needs one.
  const isExecutive = isExecutiveEmployee(employeeData);
  const showLeave =
    Boolean(onClose) &&
    (isExecutive ||
      isCoachEmployee(employeeData, departmentsWithRoles) ||
      isCoachManager(employeeData, departmentsWithRoles));
  const requiresManager = !isExecutive;
  const myLeaves = useMyLeaves({ enabled: showLeave });
  // An executive with no manager link still has an approver; say who.
  const leaveManager =
    isExecutive && myLeaves.manager.status !== "ok"
      ? { status: "executive", names: [] }
      : myLeaves.manager;
  const [applyLeaveOpen, setApplyLeaveOpen] = useState(false);
  const [leaveNotice, setLeaveNotice] = useState("");
  const [leaveFilter, setLeaveFilter] = useState("all");

  // Open edit modal scoped to a specific section (or all when null).
  const openEditSectionModal = (section) => {
    // Store snapshot of current profileData before opening modal
    profileDataSnapshotRef.current = JSON.parse(JSON.stringify(profileData));
    setEditSection(section);
    setEditModalOpen(true);
  };

  // Open full edit modal without section filter.
  const openFullEditModal = () => openEditSectionModal(null);

  // Open a PDF in a new tab with popup safeguard.
  const openPdfInNewTab = (url) => {
    if (!url) return;
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) {
      alert("Please allow popups to view the PDF.");
    }
  };

  // Handle file input change - open cropper
  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setToastMessage("Please upload a valid image file (JPG/PNG).");
      setToastType("error");
      event.target.value = "";
      return;
    }

    setSelectedImageFile(file);
    setCropperModalOpen(true);
    event.target.value = "";
  };

  // Trigger file input
  const handleEditPhotoClick = () => {
    fileInputRef.current?.click();
  };

  // Convert file to data URL
  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Upload cropped profile photo
  const handleCroppedPhotoUpload = async (croppedFile) => {
    setPhotoUploading(true);
    setCropperModalOpen(false);

    try {
      const previewDataUrl = await fileToDataUrl(croppedFile);
      const response = await uploadUserFile({
        file: croppedFile,
        category: "user_profile",
      });

      if (!response?.success) {
        throw new Error(response?.message || "Upload failed. Please try again.");
      }

      // Extract URL from response - prefer blob_url (full URL) over blob_path (relative path)
      const responseData = response.data || {};
      const photoUrl = responseData.blob_url || responseData.blob_path || responseData.url || "";
      
      
      if (!photoUrl) {
        console.error("Upload response structure:", response);
        throw new Error("Upload successful but no URL returned from server. Please try again.");
      }

      const localPreview = URL.createObjectURL(croppedFile);
      const uploadedAt = Date.now();

      const payloadToPersist = {
        profilePic: photoUrl,
        profilePicPath: photoUrl,
        profilePicUploadedAt: uploadedAt,
        profilePicPreview: previewDataUrl || "",
      };
      try {
        localStorage.setItem(
          "pending_profile_photo",
          JSON.stringify(payloadToPersist)
        );
      } catch (e) {
        // ignore localStorage failures
      }

      // Mark old photo for deletion if it exists
      const oldPhotoId = profileData.profile_photo_id;

      // Update state with the photo URL
      const updatedProfileData = {
        ...profileData,
        profilePic: photoUrl,
        profilePicPath: photoUrl,
        profilePicPreview: previewDataUrl || localPreview || photoUrl,
        profilePicUploadedAt: uploadedAt,
        profilePhotoRemoved: false,
        profilePhotoUpdated: true,
        old_photo_id_for_deletion: oldPhotoId || null,
      };

      setProfileData(updatedProfileData);

      setToastMessage("Profile photo uploaded successfully!");
      setToastType("success");

      // Auto-save the profile to persist the photo
      // Pass the updated data directly to avoid async state issues
      await handleSaveProfile(updatedProfileData);
    } catch (err) {
      console.error("Profile photo upload error:", err);
      setToastMessage(
        err?.message || "Failed to upload image. Please try again."
      );
      setToastType("error");
    } finally {
      setPhotoUploading(false);
      setSelectedImageFile(null);
    }
  };

  // Handle cropper cancel
  const handleCropperCancel = () => {
    setCropperModalOpen(false);
    setSelectedImageFile(null);
  };

  // Seed certifications list and jump to certifications editor.
  const handleQuickAddCertification = () => {
    setProfileData((prev) => ({
      ...prev,
      certifications: [
        ...(prev.certifications || []),
        {
          certification_id: "",
          certification_url: "",
          certification_url_sas: "",
          certification_view_url: "",
          other_name: "",
          description: "",
          date_received: "",
        },
      ],
    }));
    openEditSectionModal("certifications");
  };

  // Seed languages list and jump to languages editor.
  const handleQuickAddLanguage = () => {
    setProfileData((prev) => ({
      ...prev,
      languages: [
        ...(prev.languages || []),
        { language_id: "", other_name: "", description: "" },
      ],
    }));
    openEditSectionModal("languages");
  };

  // Seed specialties list and jump to specialties editor.
  const handleQuickAddSpecialty = () => {
    setProfileData((prev) => ({
      ...prev,
      professionalSpecialties: [
        ...(prev.professionalSpecialties || []),
        {
          professional_specialty_id: "",
          professional_speciality_id: "",
          other_name: "",
          description: "",
        },
      ],
    }));
    openEditSectionModal("professionalSpecialties");
  };

  // Seed achievements list and jump to achievements editor.
  const handleQuickAddAchievement = () => {
    setProfileData((prev) => ({
      ...prev,
      achievements: [
        ...(prev.achievements || []),
        { name: "", achievement_url: "", description: "", date_achieved: "" },
      ],
    }));
    openEditSectionModal("achievements");
  };

  // Body-composition maths lives in utils/bodyMetrics so the compact
  // profile strip in the Users split view computes identical numbers.

  // ---------- Helper function to transform payload to profile data ----------
  // Transforms raw API payload or cached data into component state format
  const transformPayloadToProfileData = (payload) => {
    const {
      profile,
      emails,
      user_id,
      phones,
      dietary_preferences,
      fitness_goals,
      medical_histories,
      other_medical_conditions,
      heights,
      weights,
      physical_injuries,
      certifications,
      profile_photos,
      achievements,
      languages,
      professional_specialities,
      professional_specialties,
      devices,
    } = payload;

    const latestHeight = getLatestEntry(heights);
    const latestWeight = getLatestEntry(weights);
    const latestPhoto = getLatestEntry(profile_photos);
    const latestOtherMedicalCondition = getLatestEntry(other_medical_conditions);
    const otherMedicalConditions = (
      latestOtherMedicalCondition?.other_medical_condition_description || ""
    )
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const latestDevice = getLatestEntry(devices, "last_seen_at");

    const safeDietary = Array.isArray(dietary_preferences)
      ? dietary_preferences
      : [];
    const safeFitness = Array.isArray(fitness_goals) ? fitness_goals : [];
    const safeMedical = Array.isArray(medical_histories)
      ? medical_histories
      : [];
    const safeInjuries = Array.isArray(physical_injuries)
      ? physical_injuries
      : [];
    const safeCertifications = Array.isArray(certifications)
      ? certifications
      : [];
    const profSpecs =
      professional_specialties ||
      professional_specialities ||
      [];
    const safeAchievements = Array.isArray(achievements) ? achievements : [];
    const safeLanguages = Array.isArray(languages) ? languages : [];
    const safeSpecialties = Array.isArray(profSpecs)
      ? profSpecs
      : [];

    const latestPhotoUrl =
      latestPhoto?.url ||
      latestPhoto?.img_url ||
      latestPhoto?.blob_path ||
      "";

    const mappedProfile = {
      firstName: profile?.first_name || "Unknown",
      lastName: profile?.last_name || "Unknown",
      date_of_birth: profile?.date_of_birth || "",
      email: emails?.[0]?.email_address || "Not Provided",
      phoneNumber:
        (phones?.[0]?.phone_extension?.description || "") +
        " " +
        (phones?.[0]?.phone_number || ""),
      gender: profile?.gender?.description || "Not specified",
      user_id: user_id || "Unknown",
      height: latestHeight?.height || 0,
      heightUnits: latestHeight?.height_unit?.description || "cm",
      height_id: latestHeight?.id,
      weight: latestWeight?.weight || 0,
      weightUnits: latestWeight?.weight_unit?.description || "kg",
      weight_id: latestWeight?.id,
      physicalInjury:
        safeInjuries?.[0]?.physical_injury?.description ||
        safeInjuries?.[0]?.description ||
        "None",
      physical_injury_id: safeInjuries?.[0]?.id,
      dietaryPreferences: safeDietary.map((dp) => ({
        id: dp.dietary_preference?.id || dp.id,
        description: dp.dietary_preference?.description || dp.description,
        existing_id: dp.id,
      })),
      medicalHistories: safeMedical.map((mh) => ({
        id: mh.medical_history?.id || mh.id,
        description: mh.medical_history?.description || mh.description,
        existing_id: mh.id,
      })),
      otherMedicalConditions,
      other_medical_condition_id: latestOtherMedicalCondition?.id,
      device: latestDevice
        ? {
            type: latestDevice.device_type,
            appVersion: latestDevice.app_version,
            osVersion: latestDevice.os_version,
            timezone: latestDevice.timezone,
            lastSeenAt: latestDevice.last_seen_at,
          }
        : null,
      fitnessGoals: safeFitness.map((fg) => ({
        id: fg.fitness_goal?.id || fg.id,
        description: fg.fitness_goal?.description || fg.description,
        existing_id: fg.id,
      })),
      certifications: safeCertifications.map((c) => {
        const rawUrl =
          c.certification_url ||
          c.url ||
          c.certification?.certification_url ||
          "";
        const sasUrl =
          c.certification_url_sas ||
          c.certification?.certification_url_sas ||
          "";
        return {
          id: c.certification?.id || c.certification_id || c.id,
          certification_id:
            c.certification?.id || c.certification_id || c.id,
          certification_url: rawUrl,
          certification_url_sas: sasUrl,
          certification_view_url: sasUrl || rawUrl,
          other_name: c.other_name || "",
          description: c.description || "",
          display_name:
            c.certification?.name ||
            c.certification?.description ||
            c.other_name ||
            c.description ||
            "",
          date_received: c.date_received || "",
          existing_id: c.id,
        };
      }),
      achievements: safeAchievements.map((a) => ({
        id: a.achievement_id || a.id,
        name: a.name || "",
        achievement_url: a.achievement_url || "",
        description: a.description || "",
        date_achieved: a.date_achieved || "",
        existing_id: a.id,
      })),
      languages: safeLanguages.map((l) => ({
        id: l.language?.id || l.language_id || l.id,
        language_id: l.language?.id || l.language_id || l.id,
        description: l.language?.description || l.description || "",
        other_name: l.other_name || "",
        existing_id: l.id,
      })),
      professionalSpecialties: safeSpecialties.map((p) => {
        const specId =
          p.professional_specialty?.id ||
          p.professional_speciality?.id ||
          p.professional_specialty_id ||
          p.professional_speciality_id ||
          p.id;
        const description =
          p.professional_specialty?.name ||
          p.professional_specialty?.description ||
          p.professional_speciality?.description ||
          p.description ||
          "";
        return {
          id: specId,
          professional_specialty_id: specId,
          professional_speciality_id: specId,
          description,
          other_name: p.other_name || "",
          existing_id: p.id,
        };
      }),
      age: calculateAge(profile?.date_of_birth) || "N/A",
      profilePic: latestPhotoUrl || "",
      profilePicPath:
        latestPhoto?.blob_path ||
        latestPhoto?.img_url ||
        latestPhoto?.url ||
        "",
      profilePicPreview: latestPhotoUrl || "",
      profilePicUploadedAt: latestPhoto?.created_at
        ? Date.parse(latestPhoto.created_at) || 0
        : 0,
      profile_photo_id: latestPhoto?.id,
      profilePhotoUpdated: false,
      profilePhotoRemoved: false,
    };

    // Preserve newer local/pending photo while backend catches up
    const localUploadedAt = profileData?.profilePicUploadedAt || 0;
    let pendingPhoto = null;
    try {
      const pendingPhotoRaw = localStorage.getItem("pending_profile_photo");
      pendingPhoto = pendingPhotoRaw ? JSON.parse(pendingPhotoRaw) : null;
    } catch (e) {
      pendingPhoto = null;
    }

    const newestLocal = Math.max(
      localUploadedAt || 0,
      pendingPhoto?.profilePicUploadedAt || 0
    );

    if (newestLocal && newestLocal > (mappedProfile.profilePicUploadedAt || 0)) {
      const source =
        (pendingPhoto?.profilePicUploadedAt || 0) >= (localUploadedAt || 0)
          ? pendingPhoto
          : profileData;
      mappedProfile.profilePic = source?.profilePic || mappedProfile.profilePic;
      mappedProfile.profilePicPath =
        source?.profilePicPath || mappedProfile.profilePicPath;
      mappedProfile.profilePicPreview =
        source?.profilePicPreview || mappedProfile.profilePicPreview;
      mappedProfile.profilePicUploadedAt =
        source?.profilePicUploadedAt || mappedProfile.profilePicUploadedAt;
    } else {
      if (!mappedProfile.profilePic && profileData?.profilePic) {
        mappedProfile.profilePic = profileData.profilePic;
      }
      if (!mappedProfile.profilePicPath && profileData?.profilePicPath) {
        mappedProfile.profilePicPath = profileData.profilePicPath;
      }
      if (
        !mappedProfile.profilePicPreview &&
        profileData?.profilePicPreview
      ) {
        mappedProfile.profilePicPreview = profileData.profilePicPreview;
      }
    }

    // Clear pending cache if backend now matches the saved path/url
    const backendPic =
      mappedProfile.profilePic || mappedProfile.profilePicPath;
    if (
      pendingPhoto &&
      backendPic &&
      (backendPic === pendingPhoto.profilePic ||
        backendPic === pendingPhoto.profilePicPath)
    ) {
      try {
        localStorage.removeItem("pending_profile_photo");
      } catch (e) {
        // ignore
      }
    }

    return { mappedProfile, payload };
  };

  // Fetch the profile currently shown by this card (profileId, or the
  // signed-in coach's own when omitted) and apply it to state. Shared by the
  // initial load (after a cache miss) and the post-save refresh, so both
  // always target the same profile that's actually on screen.
  const fetchAndApplyProfile = async () => {
    const eventTypeId = sessionUtils?.event_types?.[0]?.event_types?.find(
      (x) => x.description === "get_user_profile_details"
    )?.id;

    if (!eventTypeId) {
      throw new Error("Event type id for get_user_profile_details not found");
    }

    const profileRes = await getUserProfileDetails(
      eventTypeId,
      profileId || undefined
    );

    if (profileRes?.success && profileRes.data) {
      const payload = profileRes.data.data ?? profileRes.data;
      const { mappedProfile, payload: transformedPayload } =
        transformPayloadToProfileData(payload);

      setProfileData(mappedProfile);
      setOriginalData(transformedPayload);

      if (showPhotoControls) {
        profileCache.set(transformedPayload);
      }
      if (cacheKey) {
        myUserProfileCache.set(cacheKey, transformedPayload);
      }
    }
  };

  // ---------- Fetch profile data ----------
  // Load profile details and normalize into component state.
  const fetchData = async () => {
    try {
      // Only check cache for coach profiles (showPhotoControls = true)
      // User profiles should always fetch fresh data from API
      if (showPhotoControls) {
        const cached = profileCache.get();
        if (cached) {
          // Use cached data only for coach profiles
          const { mappedProfile, payload } = transformPayloadToProfileData(cached);
          setProfileData(mappedProfile);
          setOriginalData(payload);
          setLoading(false);
          return;
        }
      }

      // Client profiles are cached only where the call site opted in with a
      // cacheKey (today: the Users split view, for the coach's own clients).
      if (cacheKey) {
        const cached = myUserProfileCache.get(cacheKey);
        if (cached) {
          const { mappedProfile, payload } = transformPayloadToProfileData(cached);
          setProfileData(mappedProfile);
          setOriginalData(payload);
          setLoading(false);
          return;
        }
      }

      // Always fetch from API for user profiles (showPhotoControls = false)
      // or when no cache exists for coach profiles
      await fetchAndApplyProfile();
    } catch (err) {
      console.error("Error fetching profile:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount / when profileId or session utils change.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, sessionUtils, cacheKey]);

  // ---------- Build & submit modify payload ----------
  // Diff current profile vs original and submit modify_user_profile payload.
  // Optional parameters for photo data to handle immediate save after upload
  const handleSaveProfile = async (photoDataOverride = null, fieldOverrides = null) => {
    setSaving(true);
    try {
      const eventTypeId = sessionUtils?.event_types?.[0]?.event_types?.find(
        (x) => x.description === "modify_user_profile"
      )?.id;

      if (!eventTypeId) {
        setToastMessage("Event Type ID not found for modify_user_profile.");
        setToastType("error");
        return;
      }

      if (!originalData) {
        setToastMessage("Original profile data not loaded yet.");
        setToastType("error");
        return;
      }

      const payload = {
        event_type_id: eventTypeId,
        user_id: profileData.user_id,
      };

      // ---- Profile (basic fields) ----
      // Only include profile section if fields have actually changed
      const originalProfile = originalData?.profile || {};
      const genderId = profileUtils?.genders?.find(
        (g) =>
          g.description?.toLowerCase() === profileData.gender?.toLowerCase()
      )?.id;
      const originalGenderId = originalProfile?.gender?.id || originalProfile?.gender_id;

      // Check if any profile fields have changed
      const firstNameChanged = profileData.firstName !== (originalProfile?.first_name || "");
      const lastNameChanged = profileData.lastName !== (originalProfile?.last_name || "");
      const dobChanged = profileData.date_of_birth !== (originalProfile?.date_of_birth || "");
      const genderChanged = genderId !== originalGenderId;

      if (firstNameChanged || lastNameChanged || dobChanged || genderChanged) {
        const profilePayload = {
          operation: "UPDATE",
          first_name: profileData.firstName,
          last_name: profileData.lastName,
        };

        if (profileData.date_of_birth) {
          profilePayload.date_of_birth = formatLocalDate(
            new Date(profileData.date_of_birth)
          );
        }

        if (genderId) {
          profilePayload.gender_id = genderId;
        }

        payload.profile = profilePayload;
      }

      // ---- Height / Weight ----
      // Only include weights/heights if values have actually changed
      const originalWeight = getLatestEntry(originalData?.weights);
      const originalHeight = getLatestEntry(originalData?.heights);

      const weightUnitId = profileUtils?.weight_units?.find(
        (u) => u.description === profileData.weightUnits
      )?.id;

      const heightUnitId = profileUtils?.height_units?.find(
        (u) => u.description === profileData.heightUnits
      )?.id;

      // Check if weight has changed
      const weightChanged = 
        (weightUnitId && profileData.weight) && (
          !originalWeight || 
          originalWeight.weight !== profileData.weight ||
          originalWeight.weight_unit?.id !== weightUnitId
        );

      const weightOps = [];
      if (weightChanged) {
        weightOps.push({
          operation: profileData.weight_id ? "UPDATE" : "ADD",
          ...(profileData.weight_id && { id: profileData.weight_id }),
          weight_unit_id: weightUnitId,
          weight: profileData.weight || 0,
        });
      }
      if (weightOps.length) payload.weights = weightOps;

      // Check if height has changed
      const heightChanged = 
        (heightUnitId && profileData.height) && (
          !originalHeight || 
          originalHeight.height !== profileData.height ||
          originalHeight.height_unit?.id !== heightUnitId
        );

      const heightOps = [];
      if (heightChanged) {
        heightOps.push({
          operation: profileData.height_id ? "UPDATE" : "ADD",
          ...(profileData.height_id && { id: profileData.height_id }),
          height_unit_id: heightUnitId,
          height: profileData.height || 0,
        });
      }
      if (heightOps.length) payload.heights = heightOps;

      // ---- Physical Injuries (with "None" handling) ----
      const physicalInjuryId = profileUtils?.physical_injuries?.find(
        (pi) =>
          pi.description?.toLowerCase() ===
          profileData.physicalInjury?.toLowerCase()
      )?.id;

      const originalPhysicalInjuries = originalData?.physical_injuries || [];

      const physicalInjuryOps = [];

      if (physicalInjuryId) {
        // We want exactly ONE injury row matching selected id.
        if (profileData.physical_injury_id) {
          physicalInjuryOps.push({
            operation: "UPDATE",
            id: profileData.physical_injury_id,
            physical_injury_id: physicalInjuryId,
          });
        } else {
          physicalInjuryOps.push({
            operation: "ADD",
            physical_injury_id: physicalInjuryId,
          });
        }

        // Delete any extra injuries not matching this id
        originalPhysicalInjuries.forEach((item) => {
          if (item.id !== profileData.physical_injury_id) {
            physicalInjuryOps.push({
              operation: "DELETE",
              id: item.id,
            });
          }
        });
      } else {
        // No injury selected (e.g., "None") -> delete all existing injuries
        originalPhysicalInjuries.forEach((item) => {
          physicalInjuryOps.push({
            operation: "DELETE",
            id: item.id,
          });
        });
      }

      if (physicalInjuryOps.length) {
        payload.physical_injuries = physicalInjuryOps;
      }

      // ---- Dietary Preferences (diff: ADD / DELETE) ----
      const originalDietary = originalData?.dietary_preferences || [];
      const currentDietary = profileData.dietaryPreferences || [];

      const currentDietaryIds = currentDietary.map((dp) => dp.id);
      const originalDietaryIds = originalDietary.map(
        (dp) => dp.dietary_preference?.id || dp.id
      );

      const dietaryOps = [];

      // Deletes
      originalDietary.forEach((item) => {
        const itemId = item.dietary_preference?.id || item.id;
        if (!currentDietaryIds.includes(itemId)) {
          dietaryOps.push({
            operation: "DELETE",
            id: item.id,
          });
        }
      });

      // Adds
      currentDietary.forEach((dp) => {
        if (!originalDietaryIds.includes(dp.id)) {
          dietaryOps.push({
            operation: "ADD",
            dietary_preference_id: dp.id,
          });
        }
      });

      if (dietaryOps.length) {
        payload.dietary_preferences = dietaryOps;
      }

      // ---- Fitness Goals (diff: ADD / DELETE) ----
      const originalFitness = originalData?.fitness_goals || [];
      const currentFitness = profileData.fitnessGoals || [];

      const currentFitnessIds = currentFitness.map((fg) => fg.id);
      const originalFitnessIds = originalFitness.map(
        (fg) => fg.fitness_goal?.id || fg.id
      );

      const fitnessOps = [];

      // Deletes
      originalFitness.forEach((item) => {
        const itemId = item.fitness_goal?.id || item.id;
        if (!currentFitnessIds.includes(itemId)) {
          fitnessOps.push({
            operation: "DELETE",
            id: item.id,
          });
        }
      });

      // Adds
      currentFitness.forEach((fg) => {
        if (!originalFitnessIds.includes(fg.id)) {
          fitnessOps.push({
            operation: "ADD",
            fitness_goal_id: fg.id,
          });
        }
      });

      if (fitnessOps.length) {
        payload.fitness_goals = fitnessOps;
      }

      // ---- Medical Histories (with exclusive "None") ----
      const originalMedical = originalData?.medical_histories || [];
      const medicalNoneId = profileUtils?.medical_histories?.find(
        (mh) => mh.description?.toLowerCase() === "none"
      )?.id;

      let currentMedical = profileData.medicalHistories || [];

      // If "None" is selected, it should be exclusive
      const hasNoneSelected = currentMedical.some(
        (mh) => mh.id === medicalNoneId
      );
      if (hasNoneSelected && medicalNoneId) {
        currentMedical = currentMedical.filter((mh) => mh.id === medicalNoneId);
      }

      const currentMedicalIds = currentMedical.map((mh) => mh.id);
      const originalMedicalIds = originalMedical.map(
        (mh) => mh.medical_history?.id || mh.id
      );

      const medicalOps = [];

      // Deletes
      originalMedical.forEach((item) => {
        const itemId = item.medical_history?.id || item.id;
        if (!currentMedicalIds.includes(itemId)) {
          medicalOps.push({
            operation: "DELETE",
            id: item.id,
          });
        }
      });

      // Adds
      currentMedical.forEach((mh) => {
        if (!originalMedicalIds.includes(mh.id)) {
          medicalOps.push({
            operation: "ADD",
            medical_history_id: mh.id,
          });
        }
      });

      if (medicalOps.length) {
        payload.medical_histories = medicalOps;
      }

      // ---- Other Medical Conditions (single free-text row, chips joined with ", ") ----
      const originalOtherMedicalRow = getLatestEntry(
        originalData?.other_medical_conditions
      );
      const originalOtherMedicalDescription =
        originalOtherMedicalRow?.other_medical_condition_description || "";

      const currentOtherMedicalConditions =
        fieldOverrides?.otherMedicalConditions ?? profileData.otherMedicalConditions;

      const currentOtherMedicalDescription = (
        currentOtherMedicalConditions || []
      ).join(", ");

      const otherMedicalConditionOps = [];
      if (currentOtherMedicalDescription !== originalOtherMedicalDescription) {
        if (currentOtherMedicalDescription) {
          otherMedicalConditionOps.push({
            operation: profileData.other_medical_condition_id
              ? "UPDATE"
              : "ADD",
            ...(profileData.other_medical_condition_id && {
              id: profileData.other_medical_condition_id,
            }),
            other_medical_condition_description: currentOtherMedicalDescription,
          });
        } else if (profileData.other_medical_condition_id) {
          otherMedicalConditionOps.push({
            operation: "DELETE",
            id: profileData.other_medical_condition_id,
          });
        }
      }

      if (otherMedicalConditionOps.length) {
        payload.other_medical_conditions = otherMedicalConditionOps;
      }

      // ---- Certifications ----
      const originalCertifications = originalData?.certifications || [];
      const currentCertifications =
        fieldOverrides?.certifications ?? profileData.certifications ?? [];

      const certOps = [];

      // Deletes
      originalCertifications.forEach((item) => {
        if (!currentCertifications.some((c) => c.existing_id === item.id)) {
          certOps.push({
            operation: "DELETE",
            id: item.id,
          });
        }
      });

      // ADD / UPDATE (only if changed)
      currentCertifications.forEach((c) => {
        if (c.existing_id) {
          // Find original certification to compare
          const originalCert = originalCertifications.find(
            (oc) => oc.id === c.existing_id
          );
          
          // Check if any fields have changed
          const certificationIdChanged = 
            (c.certification_id || "") !== (originalCert?.certification?.id || originalCert?.certification_id || "");
          const certificationUrlChanged = 
            (c.certification_url || "") !== (originalCert?.certification_url || originalCert?.url || "");
          const otherNameChanged = 
            (c.other_name || "") !== (originalCert?.other_name || "");
          const descriptionChanged = 
            (c.description || "") !== (originalCert?.description || "");
          const dateReceivedChanged = 
            (c.date_received || "") !== (originalCert?.date_received || "");

          // Only add UPDATE if something changed
          if (certificationIdChanged || certificationUrlChanged || otherNameChanged || descriptionChanged || dateReceivedChanged) {
            const common = {
              certification_id: c.certification_id,
              certification_url: c.certification_url,
              ...(c.other_name && { other_name: c.other_name }),
              ...(c.description && { description: c.description }),
              ...(c.date_received && { date_received: c.date_received }),
            };
            certOps.push({
              operation: "UPDATE",
              id: c.existing_id,
              ...common,
            });
          }
        } else {
          // New certification - only ADD if it has required fields
          // Skip empty/incomplete certifications
          const hasCertificationId = c.certification_id && c.certification_id !== "";
          const hasCertificationUrl = c.certification_url && c.certification_url !== "";
          const hasOtherName = c.other_name && c.other_name.trim() !== "";
          
          // Only add if it has at least certification_id or (certification_url or other_name)
          if (hasCertificationId || (hasCertificationUrl || hasOtherName)) {
            const common = {
              certification_id: c.certification_id,
              certification_url: c.certification_url,
              ...(c.other_name && { other_name: c.other_name }),
              ...(c.description && { description: c.description }),
              ...(c.date_received && { date_received: c.date_received }),
            };
            certOps.push({
              operation: "ADD",
              ...common,
            });
          }
        }
      });

      if (certOps.length) {
        payload.certifications = certOps;
      }

      // ---- Languages ----
      const originalLanguages = originalData?.languages || [];
      const currentLanguages =
        fieldOverrides?.languages ?? profileData.languages ?? [];

      const langOps = [];
      const currentLangIds = currentLanguages.map(
        (l) => l.language_id || l.id
      );
      const originalLangIds = originalLanguages.map(
        (l) => l.language?.id || l.language_id || l.id
      );

      originalLanguages.forEach((item) => {
        const itemId = item.language?.id || item.language_id || item.id;
        if (!currentLangIds.includes(itemId)) {
          langOps.push({
            operation: "DELETE",
            id: item.id,
          });
        }
      });

      currentLanguages.forEach((l) => {
        const language_id = l.language_id || l.id;
        if (!language_id) return;
        
        if (l.existing_id) {
          // Find original language to compare
          const originalLang = originalLanguages.find(
            (ol) => ol.id === l.existing_id
          );
          
          // Check if any fields have changed
          const languageIdChanged = 
            language_id !== (originalLang?.language?.id || originalLang?.language_id || "");
          const otherNameChanged = 
            (l.other_name || "") !== (originalLang?.other_name || "");

          // Only add UPDATE if something changed
          if (languageIdChanged || otherNameChanged) {
            const common = {
              language_id,
              ...(l.other_name && { other_name: l.other_name }),
            };
            langOps.push({
              operation: "UPDATE",
              id: l.existing_id,
              ...common,
            });
          }
        } else if (!originalLangIds.includes(language_id)) {
          // New language - always ADD
          const common = {
            language_id,
            ...(l.other_name && { other_name: l.other_name }),
          };
          langOps.push({
            operation: "ADD",
            ...common,
          });
        }
      });

      if (langOps.length) {
        payload.languages = langOps;
      }

      // ---- Professional Specialties ----
      const originalSpecs =
        originalData?.professional_specialties ||
        originalData?.professional_specialities ||
        [];
      const currentSpecs =
        fieldOverrides?.professionalSpecialties ?? profileData.professionalSpecialties ?? [];

      const specOps = [];
      const currentSpecIds = currentSpecs.map(
        (s) => s.professional_specialty_id || s.professional_speciality_id || s.id
      );
      const originalSpecIds = originalSpecs.map(
        (s) =>
          s.professional_specialty?.id ||
          s.professional_speciality?.id ||
          s.professional_specialty_id ||
          s.professional_speciality_id ||
          s.id
      );

      originalSpecs.forEach((item) => {
        const itemId =
          item.professional_specialty?.id ||
          item.professional_speciality?.id ||
          item.professional_specialty_id ||
          item.professional_speciality_id ||
          item.id;
        if (!currentSpecIds.includes(itemId)) {
          specOps.push({
            operation: "DELETE",
            id: item.id,
          });
        }
      });

      currentSpecs.forEach((s) => {
        const professional_specialty_id =
          s.professional_specialty_id ||
          s.professional_speciality_id ||
          s.id;
        if (!professional_specialty_id) return;
        
        if (s.existing_id) {
          // Find original specialty to compare
          const originalSpec = originalSpecs.find(
            (os) => os.id === s.existing_id
          );
          
          const originalSpecId = 
            originalSpec?.professional_specialty?.id ||
            originalSpec?.professional_speciality?.id ||
            originalSpec?.professional_specialty_id ||
            originalSpec?.professional_speciality_id ||
            "";
          
          // Check if any fields have changed
          const specialtyIdChanged = professional_specialty_id !== originalSpecId;
          const otherNameChanged = 
            (s.other_name || "") !== (originalSpec?.other_name || "");
          const descriptionChanged = 
            (s.description || "") !== (originalSpec?.description || "");

          // Only add UPDATE if something changed
          if (specialtyIdChanged || otherNameChanged || descriptionChanged) {
            const common = {
              professional_specialty_id,
              professional_speciality_id: professional_specialty_id,
              ...(s.other_name && { other_name: s.other_name }),
              ...(s.description && { description: s.description }),
            };
            specOps.push({
              operation: "UPDATE",
              id: s.existing_id,
              ...common,
            });
          }
        } else if (!originalSpecIds.includes(professional_specialty_id)) {
          // New specialty - always ADD
          const common = {
            professional_specialty_id,
            professional_speciality_id: professional_specialty_id,
            ...(s.other_name && { other_name: s.other_name }),
            ...(s.description && { description: s.description }),
          };
          specOps.push({
            operation: "ADD",
            ...common,
          });
        }
      });

      if (specOps.length) {
        payload.professional_specialties = specOps;
      }

      // ---- Achievements ----
      const originalAchievements = originalData?.achievements || [];
      const currentAchievements =
        fieldOverrides?.achievements ?? profileData.achievements ?? [];

      const achieveOps = [];

      originalAchievements.forEach((item) => {
        if (!currentAchievements.some((a) => a.existing_id === item.id)) {
          achieveOps.push({
            operation: "DELETE",
            id: item.id,
          });
        }
      });

      currentAchievements.forEach((a) => {
        if (a.existing_id) {
          // Find original achievement to compare
          const originalAch = originalAchievements.find(
            (oa) => oa.id === a.existing_id
          );
          
          // Check if any fields have changed
          const nameChanged = 
            (a.name || "") !== (originalAch?.name || "");
          const achievementUrlChanged = 
            (a.achievement_url || "") !== (originalAch?.achievement_url || "");
          const descriptionChanged = 
            (a.description || "") !== (originalAch?.description || "");
          const dateAchievedChanged = 
            (a.date_achieved || "") !== (originalAch?.date_achieved || "");

          // Only add UPDATE if something changed
          if (nameChanged || achievementUrlChanged || descriptionChanged || dateAchievedChanged) {
            const common = {
              ...(a.name && { name: a.name }),
              ...(a.achievement_url && { achievement_url: a.achievement_url }),
              ...(a.description && { description: a.description }),
              ...(a.date_achieved && { date_achieved: a.date_achieved }),
            };
            achieveOps.push({
              operation: "UPDATE",
              id: a.existing_id,
              ...common,
            });
          }
        } else if (a.name || a.achievement_url || a.description) {
          // New achievement - always ADD
          const common = {
            ...(a.name && { name: a.name }),
            ...(a.achievement_url && { achievement_url: a.achievement_url }),
            ...(a.description && { description: a.description }),
            ...(a.date_achieved && { date_achieved: a.date_achieved }),
          };
          achieveOps.push({
            operation: "ADD",
            ...common,
          });
        }
      });

      if (achieveOps.length) {
        payload.achievements = achieveOps;
      }

      // ---- Profile Photos ----
      const originalPhoto = getLatestEntry(originalData?.profile_photos);
      const photoOps = [];

      // Use override photo data if provided (for immediate save after upload)
      // Otherwise use current profileData
      const currentPhotoData = photoDataOverride || profileData;
      const photoUrlToSave = currentPhotoData.profilePicPath || currentPhotoData.profilePic;
      const isPhotoUpdated = currentPhotoData.profilePhotoUpdated;
      const oldPhotoIdForDeletion = currentPhotoData.old_photo_id_for_deletion;
      const isPhotoRemoved = currentPhotoData.profilePhotoRemoved;
    

      // If there's an old photo to delete (when uploading new one)
      if (oldPhotoIdForDeletion && isPhotoUpdated) {
        photoOps.push({
          operation: "DELETE",
          id: oldPhotoIdForDeletion,
        });
      }

      if (photoUrlToSave && isPhotoUpdated) {
        photoOps.push({
          operation: originalPhoto?.id && !oldPhotoIdForDeletion ? "UPDATE" : "ADD",
          ...(originalPhoto?.id && !oldPhotoIdForDeletion && { id: originalPhoto.id }),
          img_url: photoUrlToSave,
        });
      } else if (
        !currentPhotoData.profilePic &&
        originalPhoto?.id &&
        isPhotoRemoved
      ) {
        photoOps.push({
          operation: "DELETE",
          id: originalPhoto.id,
        });
      }

      if (photoOps.length) {
        payload.profile_photos = photoOps;
      } else {
      }

      // ---------- API CALL ----------
      const result = await modifyUserProfile(payload);

      if (result.success) {
        // Clear snapshot on successful save
        profileDataSnapshotRef.current = null;
        setEditModalOpen(false);

        // Drop the cached payload so the next mount refetches. The edit modal
        // has already updated local state, so the visible card stays correct;
        // this only stops a stale payload being replayed on the next open.
        if (cacheKey) {
          myUserProfileCache.remove(cacheKey);
        }

        // Optimistically sync local photo
        if (photoOps.length > 0) {
          setOriginalData((prev) => ({
            ...prev,
            profile_photos: photoUrlToSave
              ? [
                  {
                    id:
                      profileData.profile_photo_id ||
                      prev?.profile_photos?.[0]?.id ||
                      "temp-local-photo",
                    img_url: photoUrlToSave,
                    created_at: new Date().toISOString(),
                  },
                ]
              : [],
          }));
          setProfileData((prev) => ({
            ...prev,
            profilePhotoUpdated: false,
            profilePhotoRemoved: false,
            old_photo_id_for_deletion: null, // Clear after successful save
          }));
        }

        // Refresh the profile actually shown on this card from the backend.
        // The saving overlay (see the outer finally below) stays up until this
        // resolves, so the card animates from old -> new data instead of
        // requiring a full page reload to show the update.
        if (showPhotoControls) {
          // Coach profiles are cached; drop the stale entry before refetching.
          profileCache.clear();
        }
        try {
          await fetchAndApplyProfile();
        } catch (err) {
          console.error("Error refreshing profile after save:", err);
        }

        // Keep the signed-in employee's own cached profile (sidebar/header) in
        // sync when this card is the one showing that same profile.
        if (refreshUserProfile && sessionUtils && (showPhotoControls || !profileId)) {
          refreshUserProfile(sessionUtils).catch((err) => {
            console.error("Error refreshing profile cache:", err);
          });
        }
      } else {
        setToastMessage(result.message || "Failed to update profile.");
        setToastType("error");
      }
    } catch (err) {
      console.error("Profile update failed:", err);
      setToastMessage("Something went wrong. Try again.");
      setToastType("error");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Toast auto-hide ----------
  // Auto-clear toast after delay.
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage("");
        setToastType("default");
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // ---------- Health Metrics Calculations ----------
  // Compute health metrics with null handling and defaults
  const healthMetrics = useMemo(() => {
    // Get values with defaults for null handling
    const height = profileData.height || 170; // Default 170 cm
    const weight = profileData.weight;
    const age = typeof profileData.age === "number" ? profileData.age : 
                 profileData.age !== "N/A" && profileData.age ? Number(profileData.age) : 30; // Default 30 years
    const gender = profileData.gender || "Male";
    const heightUnits = profileData.heightUnits || "cm";
    const weightUnits = profileData.weightUnits || "kg";

    // Calculate ideal weight first (needed for weight default)
    const idealWeight = calculateIdealWeight(height, gender, heightUnits);
    
    // Use ideal weight if actual weight is missing
    const weightForCalculations = weight || idealWeight || 70; // Fallback to 70 kg if ideal weight also fails
    
    // Calculate all metrics
    const bmi = calculateBMI(weightForCalculations, height, weightUnits, heightUnits);
    const idealWeightValue = idealWeight;
    const bmr = calculateBMR(weightForCalculations, height, age, gender, weightUnits, heightUnits);
    const tdee = calculateTDEE(bmr, 1.2); // Sedentary activity level (1.2x BMR)

    return {
      bmi: bmi !== null ? bmi : null,
      idealWeight: idealWeightValue !== null ? idealWeightValue : null,
      bmr: bmr !== null ? bmr : null,
      tdee: tdee !== null ? tdee : null,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- calculation helpers are stable
  }, [profileData.height, profileData.weight, profileData.age, profileData.gender, profileData.heightUnits, profileData.weightUnits]);

  // BMI status pill (WHO categories).
  const bmiCategory = useMemo(() => {
    const bmi = healthMetrics.bmi;
    if (bmi === null || bmi === undefined) return null;
    if (bmi < 18.5) return { label: "Underweight", className: "bg-blue-50 text-blue-600" };
    if (bmi < 25) return { label: "Normal", className: "bg-green-50 text-green-600" };
    if (bmi < 30) return { label: "Overweight", className: "bg-amber-50 text-amber-600" };
    return { label: "Obese", className: "bg-red-50 text-red-600" };
  }, [healthMetrics.bmi]);

  // Difference between actual weight and calculated ideal weight (e.g. "+3 kg").
  const idealWeightDelta =
    profileData.weight && healthMetrics.idealWeight != null
      ? Math.round(profileData.weight - healthMetrics.idealWeight)
      : null;

  // Renders a wrapping row of pills with a click-to-expand "+N more" / "less" toggle.
  const renderTagGroup = (labels, colorClassName, expanded, setExpanded, limit = 2) => {
    if (!labels || labels.length === 0) {
      return <span className="text-xs text-gray-400">None</span>;
    }
    const visibleLabels = expanded ? labels : labels.slice(0, limit);
    const hiddenCount = labels.length - limit;
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {visibleLabels.map((label, idx) => (
          <span
            key={idx}
            className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${colorClassName}`}
          >
            {label}
          </span>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
          >
            {expanded ? "less" : `+${hiddenCount} more`}
          </button>
        )}
      </div>
    );
  };

  if (loading) return <p className="text-center">Loading...</p>;
  if (error)
    return (
      <p className="text-center text-red-500">Error loading profile data.</p>
    );

  // The avatar/name/email/phone block — its own helper so the tabbed drawer
  // (enableNotesTab) can render it once above the Profile/Notes tabs instead
  // of it living only inside renderBannerContent's first row.
  const renderIdentityHeader = (stacked = false) => {
    const identityColClass = stacked
      ? "flex items-center gap-3 p-4"
      : "flex items-center gap-3 p-4 md:p-5 md:w-64 md:flex-shrink-0";
    return (
      <div className={identityColClass}>
        {showProfilePicture && (
          <div className="relative flex-shrink-0">
            <img
              src={
                profileData.profilePicPreview ||
                profileData.profilePic ||
                "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
              }
              alt={`${profileData.firstName} ${profileData.lastName}'s profile`}
              className="w-14 h-14 rounded-full object-cover border-2 border-gray-200"
            />
            {showPhotoControls && (
              <>
                <button
                  type="button"
                  onClick={handleEditPhotoClick}
                  disabled={photoUploading}
                  className="absolute -bottom-1 -right-1 bg-orange-primary border-2 border-white shadow rounded-full p-1 hover:bg-orange-600 transition text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Edit profile photo"
                >
                  <IoPencil className="w-3 h-3" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                  disabled={photoUploading}
                />
              </>
            )}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-bold text-gray-900 truncate">
            {profileData.firstName} {profileData.lastName}
          </p>
          <p className="text-xs text-gray-500 truncate">{profileData.email}</p>
          <p className="text-xs text-gray-500 truncate">{profileData.phoneNumber}</p>
        </div>
      </div>
    );
  };

  // Full banner content (top row + extended info); reused both inline and inside the collapsed overlay.
  // stacked=true lays the same sections out top-to-bottom (for the narrow slide-over drawer);
  // stacked=false (default) keeps the original wide side-by-side banner (for wide host contexts).
  // skipIdentity=true omits the identity row, for callers that render
  // `renderIdentityHeader` separately (the enableNotesTab tabbed drawer).
  const renderBannerContent = (stacked = false, skipIdentity = false) => {
    const rowClass = stacked
      ? "flex flex-col divide-y divide-gray-100"
      : "flex flex-col md:flex-row md:items-stretch gap-6 md:gap-8 md:divide-x md:divide-gray-200";
    const colClass = stacked ? "p-4" : "p-4 md:p-5 flex-1 min-w-0";
    const editColClass = stacked
      ? "flex items-center justify-end p-4"
      : "flex items-center justify-end p-4 md:pl-2";

    return (
    <>
      {/* Compact single-row profile banner */}
      <div className={rowClass}>
        {/* Column 1: identity */}
        {!skipIdentity && renderIdentityHeader(stacked)}

        {/* Column 2: biometrics */}
        <div className={colClass}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Gender</p>
              <p className="text-sm md:text-base font-bold text-gray-900">{profileData.gender}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Age</p>
              <p className="text-sm md:text-base font-bold text-gray-900">{profileData.age} years</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Height</p>
              <p className="text-sm md:text-base font-bold text-gray-900">
                {profileData.height} {profileData.heightUnits || "cm"}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Weight</p>
              <p className="text-sm md:text-base font-bold text-gray-900">
                {profileData.weight} {profileData.weightUnits || "kg"}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Physical Injury</p>
              <p className="text-sm md:text-base font-bold text-gray-900">
                {profileData.physicalInjury || "None"}
              </p>
            </div>
          </div>
        </div>

        {/* Column 3: biometrics & energy */}
        <div className={colClass}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">BMI</p>
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="text-sm md:text-base font-bold text-gray-900">
                  {healthMetrics.bmi !== null ? healthMetrics.bmi : "N/A"}
                </p>
                {bmiCategory && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${bmiCategory.className}`}>
                    {bmiCategory.label}
                  </span>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Ideal Weight</p>
              <p className="text-sm md:text-base font-bold text-gray-900">
                {healthMetrics.idealWeight !== null ? `${healthMetrics.idealWeight} kg` : "N/A"}
                {idealWeightDelta !== null && idealWeightDelta !== 0 && (
                  <span className="text-xs font-medium text-gray-400 ml-1">
                    ({idealWeightDelta > 0 ? "+" : ""}{idealWeightDelta} kg)
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">BMR</p>
              <p className="text-sm md:text-base font-bold text-gray-900">
                {healthMetrics.bmr !== null ? `${healthMetrics.bmr} kcal/d` : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">TDEE</p>
              <p className="text-sm md:text-base font-bold text-gray-900">
                {healthMetrics.tdee !== null ? `${healthMetrics.tdee} kcal/d` : "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Column 4: dietary & focus tags */}
        <div className={`${colClass} space-y-2`}>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Dietary</p>
            {renderTagGroup(
              profileData.dietaryPreferences?.map((dp) => dp.description) || [],
              "bg-indigo-50 text-indigo-600",
              dietaryTagsExpanded,
              setDietaryTagsExpanded
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Focus</p>
            {renderTagGroup(
              profileData.fitnessGoals?.map((fg) => fg.description) || [],
              "bg-orange-50 text-orange-600",
              focusTagsExpanded,
              setFocusTagsExpanded
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Medical</p>
            {renderTagGroup(
              profileData.medicalHistories?.map((mh) => mh.description) || [],
              "bg-rose-50 text-rose-700 border border-rose-100",
              medicalTagsExpanded,
              setMedicalTagsExpanded
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Other Medical Conditions</p>
            {renderTagGroup(
              profileData.otherMedicalConditions || [],
              "bg-rose-50 text-rose-700 border border-rose-100",
              otherMedicalTagsExpanded,
              setOtherMedicalTagsExpanded
            )}
          </div>
        </div>

        {/* Edit - far right (stacked/drawer variant renders this in its own header instead) */}
        {!stacked && (
          <div className={editColClass}>
            <button
              type="button"
              onClick={openFullEditModal}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-50 hover:bg-orange-100 text-orange-primary border border-orange-200 transition-colors flex-shrink-0"
              aria-label="Edit profile"
              title="Edit profile"
            >
              <IoPencil className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Extended profile info - only for the coach's own profile */}
      <div className="text-sm text-gray-800">
        {showPhotoControls && (
          <div className="border-t border-gray-200 p-6 grid grid-cols-1 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm relative">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-800">
                  Certifications
                </h3>
                {showPhotoControls && (
                  <button
                    type="button"
                    onClick={handleQuickAddCertification}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-50 hover:bg-orange-100 text-orange-primary border border-orange-200 transition-colors"
                    aria-label="Add certification"
                    title="Add certification"
                  >
                    <IoAdd className="w-5 h-5" />
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {profileData.certifications?.length > 0 ? (
                  profileData.certifications.map((c, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-gray-200 p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-800 mb-1">
                            {c.display_name ||
                              c.other_name ||
                              c.description ||
                              "Certification"}
                          </div>
                          {c.date_received && (
                            <p className="text-xs text-gray-500 mb-2">
                              Received: {c.date_received.slice(0, 10)}
                            </p>
                          )}
                          {(c.certification_view_url ||
                            c.certification_url_sas ||
                            c.certification_url) && (
                            <button
                              type="button"
                              onClick={() =>
                                openPdfInNewTab(
                                  c.certification_view_url ||
                                    c.certification_url_sas ||
                                    c.certification_url
                                )
                              }
                              className="text-xs text-orange-primary hover:text-orange-700 hover:underline font-medium"
                            >
                              View Certificate →
                            </button>
                          )}
                        </div>
                        {showPhotoControls && (
                          <button
                            type="button"
                            onClick={() => openEditSectionModal("certifications")}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 transition-colors"
                            aria-label="Edit certification"
                            title="Edit certification"
                          >
                            <IoPencil className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No certifications added</p>
                )}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm relative">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-800">
                  Languages
                </h3>
                {showPhotoControls && (
                  <button
                    type="button"
                    onClick={handleQuickAddLanguage}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-50 hover:bg-orange-100 text-orange-primary border border-orange-200 transition-colors"
                    aria-label="Add language"
                    title="Add language"
                  >
                    <IoAdd className="w-5 h-5" />
                  </button>
                )}
              </div>
              {profileData.languages?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profileData.languages
                    .map((lang, idx) => {
                      const languageId = lang.language_id || lang.id;
                      const languageName = lang.description ||
                        lang.other_name ||
                        profileUtils?.languages?.find(l => l.id === languageId)?.description ||
                        null;
                      return { lang, idx, languageName, languageId };
                    })
                    .filter(({ languageName }) => languageName && languageName.trim() !== "")
                    .map(({ lang: _lang, idx, languageName, languageId: _languageId }) => (
                      <div
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full text-sm font-medium text-gray-700"
                      >
                        <span>{languageName}</span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No languages added</p>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm relative">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-800">
                  Professional Specialties
                </h3>
                {showPhotoControls && (
                  <button
                    type="button"
                    onClick={handleQuickAddSpecialty}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-50 hover:bg-orange-100 text-orange-primary border border-orange-200 transition-colors"
                    aria-label="Add professional specialty"
                    title="Add professional specialty"
                  >
                    <IoAdd className="w-5 h-5" />
                  </button>
                )}
              </div>
              {profileData.professionalSpecialties?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {profileData.professionalSpecialties
                    .map((spec, idx) => {
                      const specialtyId = spec.professional_specialty_id || spec.professional_speciality_id || spec.id;
                      const specialtyName = spec.description ||
                        spec.other_name ||
                        profileUtils?.professional_specialties?.find(s => s.id === specialtyId)?.description ||
                        null;
                      return { spec, idx, specialtyName, specialtyId };
                    })
                    .filter(({ specialtyName }) => specialtyName && specialtyName.trim() !== "")
                    .map(({ spec: _spec, idx, specialtyName, specialtyId: _specialtyId }) => (
                      <div
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-full text-sm font-medium text-gray-700"
                      >
                        <span>{specialtyName}</span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No specialties added</p>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm relative">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-800">
                  Achievements
                </h3>
                {showPhotoControls && (
                  <button
                    type="button"
                    onClick={handleQuickAddAchievement}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-orange-50 hover:bg-orange-100 text-orange-primary border border-orange-200 transition-colors"
                    aria-label="Add achievement"
                    title="Add achievement"
                  >
                    <IoAdd className="w-5 h-5" />
                  </button>
                )}
              </div>
              {profileData.achievements?.length > 0 ? (
                <div className="space-y-3">
                  {profileData.achievements.map((ach, idx) => (
                    <div
                      key={idx}
                      className="border-l-4 border-orange-primary pl-4 py-2"
                    >
                      <p className="text-sm font-medium text-gray-800 mb-1">
                        {ach.description || "Achievement"}
                      </p>
                      {ach.name && (
                        <p className="text-xs text-gray-500 mb-1">
                          {ach.name}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        {ach.date_achieved && (
                          <span>{ach.date_achieved.slice(0, 10)}</span>
                        )}
                        {ach.achievement_url && (
                          <a
                            href={ach.achievement_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-primary hover:underline"
                          >
                            View link
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No achievements added</p>
              )}
            </div>
          </div>
        )}

        {profileData.device && (
          <div className="border-t border-gray-200 p-6">
            <DeviceActivityCard device={profileData.device} />
          </div>
        )}
      </div>
    </>
    );
  };

  // Shared saving-spinner overlay, rendered wherever the banner content lives.
  const renderSavingOverlay = () =>
    saving && (
      <div className="absolute inset-0 bg-white bg-opacity-90 backdrop-blur-sm flex items-center justify-center z-50 rounded-xl">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-primary"></div>
          <p className="text-gray-700 font-medium">Saving profile...</p>
        </div>
      </div>
    );

  // Edit-profile modal + photo cropper modal - independent of the banner/drawer, always mounted the same way.
  const renderModals = () => (
    <>
      <EditProfileCard
        isOpen={editModalOpen}
        onClose={() => {
          // Reset profileData to snapshot if modal is closed without saving
          if (profileDataSnapshotRef.current) {
            // Preserve photo-related state if photo was uploaded
            const photoState = {
              profilePic: profileData.profilePic,
              profilePicPath: profileData.profilePicPath,
              profilePicPreview: profileData.profilePicPreview,
              profilePicUploadedAt: profileData.profilePicUploadedAt,
              profile_photo_id: profileData.profile_photo_id,
              profilePhotoUpdated: profileData.profilePhotoUpdated,
              profilePhotoRemoved: profileData.profilePhotoRemoved,
              old_photo_id_for_deletion: profileData.old_photo_id_for_deletion,
            };
            setProfileData({
              ...profileDataSnapshotRef.current,
              ...photoState,
            });
            profileDataSnapshotRef.current = null;
          }
          setEditModalOpen(false);
          setEditSection(null);
        }}
        profileData={profileData}
        setProfileData={setProfileData}
        showPhotoControls={showPhotoControls}
        onSave={handleSaveProfile}
        sectionFilter={editSection}
      />

      {/* Image Cropper Modal */}
      {cropperModalOpen && selectedImageFile && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-gray-900">
                Crop Profile Photo
              </h2>
              <button
                onClick={handleCropperCancel}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close cropper"
              >
                <IoClose className="w-6 h-6" />
              </button>
            </div>
            <ImageCropper
              imageFile={selectedImageFile}
              onCropComplete={handleCroppedPhotoUpload}
              onCancel={handleCropperCancel}
              aspectRatio={3 / 4} // 3:4 ratio (width:height)
              maxSizeMB={0.5}
              showCompression={true}
            />
          </div>
        </div>
      )}
    </>
  );

  // ---------- Own-profile modal (Claude Design "Profile Modal Redesign" 4a) ----------
  // Professional items are saved the moment they are added or removed: the new
  // list goes to handleSaveProfile as an override, so the diff doesn't depend
  // on the setProfileData below having landed yet.
  const saveProfessionalList = (key, next) => {
    setProfileData((prev) => ({ ...prev, [key]: next }));
    return handleSaveProfile(null, { [key]: next });
  };

  // Both "Apply leave" buttons land on the Leave tab. A coach or manager with
  // no manager assigned gets the error there instead of the form — the API
  // would accept the request, but nobody below an executive could act on it.
  const handleApplyLeave = () => {
    setOwnTab("leave");
    closeInlineAdd();
    if (requiresManager && leaveManager.status === "none") {
      setLeaveNotice(NO_MANAGER_MESSAGE);
      return;
    }
    if (requiresManager && leaveManager.status === "error") {
      setLeaveNotice("Couldn't check who your manager is. Close your profile and open it again.");
      return;
    }
    setLeaveNotice("");
    setApplyLeaveOpen(true);
  };

  const closeInlineAdd = () => {
    setAddingSection(null);
    setAddDraft("");
  };

  const languageIdOf = (l) => l.language_id || l.id;
  const specialtyIdOf = (s) =>
    s.professional_specialty_id || s.professional_speciality_id || s.id;

  // One entry per Professional row. `options` (lookup-backed sections) makes
  // the inline add a select; achievements take free text; certifications need
  // a file upload, so "+ Add" opens the section editor instead.
  const professionalSections = () => {
    const languageOptions = (profileUtils?.languages || []).filter(
      (o) => !(profileData.languages || []).some((l) => languageIdOf(l) === o.id)
    );
    const specialtyOptions = (profileUtils?.professional_specialties || []).filter(
      (o) =>
        !(profileData.professionalSpecialties || []).some((s) => specialtyIdOf(s) === o.id)
    );
    return [
      {
        key: "certifications",
        title: "Certifications",
        empty: "No certifications added",
        items: (profileData.certifications || []).map((c) => ({
          label: c.display_name || c.other_name || c.description || "Certification",
          url: c.certification_view_url || c.certification_url_sas || c.certification_url,
        })),
        onAdd: handleQuickAddCertification,
      },
      {
        key: "languages",
        title: "Languages",
        empty: "No languages added",
        items: (profileData.languages || []).map((l) => ({
          label:
            l.description ||
            l.other_name ||
            profileUtils?.languages?.find((o) => o.id === languageIdOf(l))?.description ||
            "",
        })),
        options: languageOptions,
        placeholder: "Choose a language",
        build: (option) => ({
          language_id: option.id,
          id: option.id,
          description: option.description,
          other_name: "",
          existing_id: null,
        }),
      },
      {
        key: "professionalSpecialties",
        title: "Professional specialties",
        empty: "No specialties added",
        items: (profileData.professionalSpecialties || []).map((s) => ({
          label:
            s.description ||
            s.other_name ||
            profileUtils?.professional_specialties?.find((o) => o.id === specialtyIdOf(s))
              ?.description ||
            "",
        })),
        options: specialtyOptions,
        placeholder: "Choose a specialty",
        build: (option) => ({
          professional_specialty_id: option.id,
          professional_speciality_id: option.id,
          id: option.id,
          description: option.description,
          other_name: "",
          existing_id: null,
        }),
      },
      {
        key: "achievements",
        title: "Achievements",
        empty: "No achievements added",
        items: (profileData.achievements || []).map((a) => ({
          label: a.description || a.name || "Achievement",
        })),
        placeholder: "e.g. Ironman finisher 2024",
        build: (text) => ({
          name: "",
          achievement_url: "",
          description: text,
          date_achieved: "",
        }),
      },
    ];
  };

  const commitInlineAdd = (section) => {
    const value = addDraft.trim();
    if (!value) return;
    let entry;
    if (section.options) {
      const option = section.options.find((o) => o.id === value);
      if (!option) return;
      entry = section.build(option);
    } else {
      entry = section.build(value);
    }
    closeInlineAdd();
    saveProfessionalList(section.key, [...(profileData[section.key] || []), entry]);
  };

  const removeProfessionalItem = (key, index) => {
    saveProfessionalList(
      key,
      (profileData[key] || []).filter((_, i) => i !== index)
    );
  };

  // BMI tile pill, in the design's palette. Unlike `healthMetrics` (which
  // falls back to defaults for the client drawer), the tile shows "—" until
  // both height and weight are on file.
  const hasBodyMeasurements = profileData.height > 0 && profileData.weight > 0;
  const bmiPill = (() => {
    if (!hasBodyMeasurements || healthMetrics.bmi == null) {
      return { label: "Add height & weight", className: "text-[#C2410C] bg-[#FFF0E6]" };
    }
    const bmi = healthMetrics.bmi;
    if (bmi < 18.5) return { label: "Underweight", className: "text-[#9A5B00] bg-[#FFF4DB]" };
    if (bmi < 25) return { label: "Normal", className: "text-[#2F8A4E] bg-[#E6F4EA]" };
    if (bmi < 30) return { label: "Overweight", className: "text-[#9A5B00] bg-[#FFF4DB]" };
    return { label: "Obese", className: "text-[#B42318] bg-[#FDECEA]" };
  })();
  const formatKcal = (value) =>
    hasBodyMeasurements && value != null ? Number(value).toLocaleString("en-IN") : "—";

  const joinOrNone = (labels) => (labels && labels.length ? labels.join(", ") : "None");

  const renderOwnProfile = () => {
    const fullName = `${profileData.firstName} ${profileData.lastName}`.trim();
    const initials = `${profileData.firstName?.[0] || ""}${profileData.lastName?.[0] || ""}`.toUpperCase();
    const photo = profileData.profilePicPreview || profileData.profilePic;
    const genderAge = [
      profileData.gender !== "Not specified" ? profileData.gender : null,
      profileData.age && profileData.age !== "N/A" ? `${profileData.age} years` : null,
    ]
      .filter(Boolean)
      .join(" · ");
    const phone = (profileData.phoneNumber || "").trim();

    const tabs = [
      { id: "health", label: "Health & metrics" },
      ...(showPhotoControls ? [{ id: "pro", label: "Professional" }] : []),
      ...(showLeave ? [{ id: "leave", label: "Leave", badge: myLeaves.pendingCount }] : []),
    ];
    const activeTab = tabs.some((t) => t.id === ownTab) ? ownTab : "health";

    const healthRows = [
      {
        label: "Height",
        value: profileData.height > 0 ? `${profileData.height} ${profileData.heightUnits || "cm"}` : null,
        add: "Add height",
      },
      {
        label: "Weight",
        value: profileData.weight > 0 ? `${profileData.weight} ${profileData.weightUnits || "kg"}` : null,
        add: "Add weight",
      },
      {
        label: "Ideal weight",
        value: healthMetrics.idealWeight != null ? `${healthMetrics.idealWeight} kg` : null,
        add: "Add height",
      },
      { label: "Fitness goals", value: joinOrNone(profileData.fitnessGoals?.map((g) => g.description)) },
      {
        label: "Dietary preferences",
        value: joinOrNone(profileData.dietaryPreferences?.map((d) => d.description)),
      },
      { label: "Physical injury", value: profileData.physicalInjury || "None" },
      {
        label: "Medical history",
        value: joinOrNone(profileData.medicalHistories?.map((m) => m.description)),
      },
      { label: "Other medical conditions", value: joinOrNone(profileData.otherMedicalConditions) },
    ];

    return (
      <div className="h-full grid grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)] overflow-y-auto md:overflow-hidden text-[#1F2328]">
        {/* Left rail: identity, contact, actions */}
        <div className="bg-[#FAF8F6] border-b md:border-b-0 md:border-r border-[#EFEBE7] px-6 py-7 flex flex-col gap-5">
          <div className="flex flex-col items-center gap-2.5 text-center">
            <div className="relative w-[88px] h-[88px]">
              {showProfilePicture && photo ? (
                <img
                  src={photo}
                  alt={`${fullName}'s profile`}
                  className="w-[88px] h-[88px] rounded-full object-cover"
                />
              ) : (
                <div className="w-[88px] h-[88px] rounded-full bg-[#E9DFD6] flex items-center justify-center text-[28px] font-extrabold text-[#8A6A52]">
                  {initials}
                </div>
              )}
              {showPhotoControls && (
                <>
                  <button
                    type="button"
                    onClick={handleEditPhotoClick}
                    disabled={photoUploading}
                    title="Change photo"
                    aria-label="Change profile photo"
                    className="absolute right-0 bottom-0 w-7 h-7 rounded-full border-2 border-white bg-[#F2601F] text-white flex items-center justify-center hover:bg-[#E8590C] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <IoPencil className="w-3 h-3" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={photoUploading}
                  />
                </>
              )}
            </div>
            <div className="text-[19px] font-extrabold break-words">{fullName}</div>
            {genderAge && <div className="text-sm text-[#5C554F]">{genderAge}</div>}
          </div>

          <div className="flex flex-col gap-2.5 text-sm border-t border-[#EFEBE7] pt-4">
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-xs text-[#8A8178] font-semibold">Email</span>
              <span className="font-semibold text-[#1F2328] truncate">{profileData.email}</span>
            </div>
            {phone && (
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-xs text-[#8A8178] font-semibold">Phone</span>
                <span className="font-semibold text-[#1F2328] truncate">{phone}</span>
              </div>
            )}
          </div>

          <div className="mt-auto flex flex-col gap-2">
            {showLeave && (
              <button
                type="button"
                onClick={handleApplyLeave}
                disabled={leaveManager.status === "loading"}
                className="w-full h-10 rounded-lg border border-[#F2601F] bg-white text-[#E8590C] text-sm font-bold hover:bg-[#FFF6F0] disabled:opacity-60"
              >
                Apply leave
              </button>
            )}
            <button
              type="button"
              onClick={() => openEditSectionModal("basic")}
              className="w-full h-10 rounded-lg bg-[#F2601F] text-white text-sm font-bold hover:bg-[#E8590C]"
            >
              Edit profile
            </button>
          </div>
        </div>

        {/* Right pane: tabs + content */}
        <div className="flex flex-col min-h-0">
          <div className="flex items-center gap-1 px-6 pt-4 border-b border-[#EFEBE7]">
            <div role="tablist" aria-label="Profile sections" className="flex items-center gap-1">
              {tabs.map((t) => {
                const on = t.id === activeTab;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => {
                      setOwnTab(t.id);
                      closeInlineAdd();
                    }}
                    className={`h-11 px-3.5 text-[15px] font-bold flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap ${
                      on ? "text-[#1F2328] border-[#F2601F]" : "text-[#8A8178] border-transparent hover:text-[#5C554F]"
                    }`}
                  >
                    {t.label}
                    {t.badge > 0 && (
                      <span className="text-[11px] font-extrabold bg-[#FFF0E6] text-[#C2410C] px-[7px] py-px rounded-full">
                        {t.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close profile"
              className="w-9 h-9 mb-2 rounded-lg border border-[#E8E5E1] bg-white text-[#5C554F] flex items-center justify-center hover:bg-[#F4F1EE] flex-shrink-0"
            >
              <IoClose className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 md:overflow-y-auto flex-1">
            {activeTab === "health" && (
              <div className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div className="bg-[#FFF6F0] rounded-xl p-4 flex flex-col gap-1">
                    <span className="text-[13px] text-[#8A6A52] font-bold">BMI</span>
                    <span className="text-[26px] font-extrabold">
                      {hasBodyMeasurements && healthMetrics.bmi != null ? healthMetrics.bmi : "—"}
                    </span>
                    <span className={`self-start text-xs font-bold px-2 py-0.5 rounded-full ${bmiPill.className}`}>
                      {bmiPill.label}
                    </span>
                  </div>
                  {[
                    ["BMR", healthMetrics.bmr],
                    ["TDEE", healthMetrics.tdee],
                  ].map(([label, value]) => (
                    <div key={label} className="bg-[#FAF8F6] rounded-xl p-4 flex flex-col gap-1">
                      <span className="text-[13px] text-[#8A8178] font-bold">{label}</span>
                      <span className="text-[26px] font-extrabold">{formatKcal(value)}</span>
                      <span className="text-[13px] text-[#5C554F]">kcal / day</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col">
                  {healthRows.map((r) => (
                    <div
                      key={r.label}
                      className="flex justify-between gap-4 py-3 border-b border-[#F1EEEA] text-sm"
                    >
                      <span className="text-[#5C554F]">{r.label}</span>
                      {r.value ? (
                        <span
                          className={`font-bold text-right max-w-[60%] ${
                            r.value === "None" ? "text-[#B8B0A8]" : "text-[#1F2328]"
                          }`}
                        >
                          {r.value}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openEditSectionModal("basic")}
                          className="text-sm font-bold text-[#E8590C] hover:text-[#C2410C]"
                        >
                          {r.add}
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {profileData.device && <DeviceActivityCard device={profileData.device} />}
              </div>
            )}

            {activeTab === "leave" && (
              <LeaveTab
                leaves={myLeaves.leaves}
                loading={myLeaves.loading}
                error={myLeaves.error}
                onRetry={myLeaves.reload}
                onApply={handleApplyLeave}
                manager={leaveManager}
                notice={leaveNotice}
                filter={leaveFilter}
                onFilterChange={setLeaveFilter}
                onWithdraw={async (leave) => {
                  await myLeaves.withdraw(leave);
                  setToastMessage("Request withdrawn");
                  setToastType("success");
                }}
              />
            )}

            {activeTab === "pro" && (
              <div className="flex flex-col">
                {professionalSections().map((s) => {
                  const adding = addingSection === s.key;
                  return (
                    <div
                      key={s.key}
                      role="group"
                      aria-label={s.title}
                      className="flex flex-col gap-2.5 py-4 border-b border-[#F1EEEA]"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-[10px] bg-[#FFF0E6] text-[#E8590C] flex items-center justify-center font-extrabold text-[15px] flex-shrink-0">
                          {s.title[0]}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                          <span className="text-[15px] font-bold">{s.title}</span>
                          <span className="text-[13px] text-[#8A8178]">
                            {s.items.length ? `${s.items.length} added` : s.empty}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (s.onAdd) {
                              closeInlineAdd();
                              s.onAdd();
                            } else {
                              setAddingSection(s.key);
                              setAddDraft("");
                            }
                          }}
                          className="h-[34px] px-3.5 rounded-lg border border-[#E8E5E1] bg-white text-[13px] font-bold hover:bg-[#FAF8F6] flex-shrink-0"
                        >
                          + Add
                        </button>
                      </div>

                      {s.items.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pl-14">
                          {s.items.map((it, i) => (
                            <span
                              key={`${s.key}-${i}`}
                              className="h-[30px] pl-3 pr-1.5 rounded-full bg-[#FAF8F6] border border-[#EFEBE7] text-[13px] font-semibold flex items-center gap-1 max-w-full"
                            >
                              {it.url ? (
                                <button
                                  type="button"
                                  onClick={() => openPdfInNewTab(it.url)}
                                  title="View certificate"
                                  className="truncate hover:text-[#E8590C]"
                                >
                                  {it.label}
                                </button>
                              ) : (
                                <span className="truncate">{it.label}</span>
                              )}
                              <button
                                type="button"
                                onClick={() => removeProfessionalItem(s.key, i)}
                                disabled={saving}
                                aria-label={`Remove ${it.label}`}
                                className="w-5 h-5 rounded-full text-[#8A8178] flex items-center justify-center hover:bg-[#EFEBE7] flex-shrink-0"
                              >
                                <IoClose className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {adding && (
                        <div className="flex gap-2 pl-14">
                          {s.options ? (
                            <select
                              autoFocus
                              value={addDraft}
                              onChange={(e) => setAddDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitInlineAdd(s);
                                if (e.key === "Escape") closeInlineAdd();
                              }}
                              className="flex-1 min-w-0 h-9 border border-[#F2601F] shadow-[0_0_0_3px_#FFE3D2] rounded-lg px-2.5 text-sm outline-none bg-white"
                            >
                              <option value="">
                                {s.options.length ? s.placeholder : "Nothing left to add"}
                              </option>
                              {s.options.map((o) => (
                                <option key={o.id} value={o.id}>
                                  {o.description}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              autoFocus
                              value={addDraft}
                              onChange={(e) => setAddDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitInlineAdd(s);
                                if (e.key === "Escape") closeInlineAdd();
                              }}
                              placeholder={s.placeholder}
                              className="flex-1 min-w-0 h-9 border border-[#F2601F] shadow-[0_0_0_3px_#FFE3D2] rounded-lg px-3 text-sm outline-none"
                            />
                          )}
                          <button
                            type="button"
                            onClick={() => commitInlineAdd(s)}
                            disabled={!addDraft.trim()}
                            className="h-9 px-3.5 rounded-lg bg-[#F2601F] text-white text-[13px] font-bold disabled:bg-[#F6B48F]"
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            onClick={closeInlineAdd}
                            className="h-9 px-3 rounded-lg border border-[#E8E5E1] bg-white text-[13px] font-bold text-[#5C554F]"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Embedded inside a host-managed panel (AppSidebar's own profile modal passes onClose) -
  // render the plain content directly; the host already provides the panel/backdrop/close chrome.
  if (onClose) {
    return (
      <div className="relative h-full">
        {toastMessage && <Toaster message={toastMessage} type={toastType} />}
        {renderSavingOverlay()}
        {renderOwnProfile()}
        {renderModals()}
        {showLeave && (
          <ApplyLeaveModal
            isOpen={applyLeaveOpen}
            onClose={() => setApplyLeaveOpen(false)}
            leaveTypes={myLeaves.leaveTypes}
            countBookings={myLeaves.countBookings}
            overlapFor={myLeaves.overlapFor}
            onSubmit={async (fields) => {
              await myLeaves.apply(fields);
              setApplyLeaveOpen(false);
              setLeaveFilter("all");
              setToastMessage("Leave request submitted");
              setToastType("success");
            }}
          />
        )}
      </div>
    );
  }

  // Standalone usage: a floating icon (always visible, fixed to the viewport) toggles a
  // right-hand slide-over panel that overlays the page instead of pushing its layout around.
  return (
    <>
      <button
        type="button"
        onClick={() => setIsDrawerOpen((prev) => !prev)}
        className="fixed top-4 right-4 z-40 flex items-center gap-2 pl-1 pr-4 py-1 rounded-full bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-shadow max-w-[16rem]"
        aria-label={isDrawerOpen ? "Close client profile" : "View client profile"}
        title="Client profile"
      >
        {showProfilePicture && (
          <img
            src={
              profileData.profilePicPreview ||
              profileData.profilePic ||
              "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
            }
            alt={`${profileData.firstName} ${profileData.lastName}'s profile`}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
          />
        )}
        <span className="text-sm font-semibold text-gray-900 truncate">
          {profileData.firstName} {profileData.lastName}
        </span>
      </button>

      <div
        className={`fixed inset-y-0 right-0 z-40 w-full sm:max-w-2xl bg-white shadow-2xl overflow-y-auto transform transition-transform duration-300 ease-in-out ${
          isDrawerOpen ? "translate-x-0 pointer-events-auto" : "translate-x-full pointer-events-none"
        }`}
      >
        <div className="relative">
          {toastMessage && <Toaster message={toastMessage} type={toastType} />}
          {renderSavingOverlay()}
          <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
            <button
              type="button"
              onClick={openFullEditModal}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-orange-primary hover:bg-orange-600 text-white text-sm font-medium shadow-sm transition-colors"
              aria-label="Edit profile"
            >
              <IoPencil className="w-4 h-4" />
              Edit Profile
            </button>
            <button
              type="button"
              onClick={() => setIsDrawerOpen(false)}
              className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full shadow-sm"
              aria-label="Close profile"
            >
              <IoClose className="w-4 h-4" />
            </button>
          </div>
          {enableNotesTab ? (
            <>
              {renderIdentityHeader(true)}
              <div className="px-4">
                <Tabs
                  value={activeCardTab}
                  onChange={setActiveCardTab}
                  items={[
                    { id: "profile", label: "Profile" },
                    { id: "notes", label: "Notes" },
                  ]}
                  label="Profile sections"
                  idPrefix="profile-card-tab"
                />
              </div>
              {activeCardTab === "notes" ? (
                <div className="p-4">
                  <NotesTab
                    profileId={profileId}
                    variant="panel"
                    cacheKey={serializeUserNotesKey(profileId)}
                  />
                </div>
              ) : (
                renderBannerContent(true, true)
              )}
            </>
          ) : (
            renderBannerContent(true)
          )}
        </div>
      </div>

      {renderModals()}
    </>
  );
};

export default ProfileCard;
